import React, {MouseEvent, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Sync} from "@mui/icons-material";

import {
    ACard,
    ADataGrid,
    ADataGridFilter,
    PagedSearchParams,
    Period,
    ASectionTitle,
} from "@atiautomacao/ati-ui-library";
import {
    Box, ButtonGroup, CircularProgress, FormControl, FormHelperText,
    Grid,
    LinearProgress,
    linearProgressClasses,
    Link,
    styled, TextField,
    Typography, useMediaQuery, useTheme,
} from "@mui/material";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {clear, Performance, searchAnalisysData, searchPowerStations} from "./PerformanceAnalysisReducer";
import GeneratedInjectedChart from "./GeneratedInjectedChart";
import IrradianceChart from "./IrradianceChart";
import {usePowerStationNavigation} from '../../../Shared/Hooks/usePowerStationNavigation';
import {getIconColor} from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import {MenuItemStatus} from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import {performanceAnalysisColumns} from "./PerformanceAnalysisConfig";
import useInterval from "../../../Shared/Hooks/useInterval";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../Config/Constants";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import {differenceInDays, startOfDay, subDays} from "date-fns";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import ActivePowerChart from "./ActivePowerChart";
import {getStatusName} from "../../../Shared/Components/Layout/menu/helpers/getStatusName";
import {validateNumber} from "../../../Utils/RegexUtils";
import { LoadingButton } from "@mui/lab";

const DIFFERENCE_MAX_DAYS = 31;
const PerformanceAnalysisPage = () : ReactElement => {
        const theme = useTheme();
        const extraSmallScreen = useMediaQuery(theme.breakpoints.only("xs"));
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByPdf = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_PDF_EXPORT, AUTHORITIES.EXPORT_PERFORMANCE_ANALYSIS, AUTHORITIES.PDF_EXPORT_PERFORMANCE_ANALYSIS
    ]);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.EXPORT_PERFORMANCE_ANALYSIS, AUTHORITIES.CSV_EXPORT_PERFORMANCE_ANALYSIS
    ]);
    const isAuthorizedToSearchByExcel = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_EXCEL_EXPORT, AUTHORITIES.EXPORT_PERFORMANCE_ANALYSIS, AUTHORITIES.EXCEL_EXPORT_PERFORMANCE_ANALYSIS
    ]);

    const initialPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(new Date().setHours(0, 0, 0, 0)),
        toDateTime: new Date()
    };

    const [period, setPeriod] = useState<Period>(initialPeriod);

    const dispatch = useAppDispatch();
    const { entities, loading, powerStations } = useAppSelector((state: RootState) => state.performanceAnalysis);
    const totalOfRecords = useAppSelector((state : RootState) => state.performanceAnalysis.totalOfRecords);
    const navigationToPowerStation = usePowerStationNavigation();
    const entityMap =  useRef(new Map<number, Performance>()).current;

    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const [rows, setRows] = useState<Array<any>>([]);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(performanceAnalysisColumns), 0, userConfig.sizePagePerformanceAnalysis));

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams: PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    },[]);

    const getPeriodFilter = async () => {
        const cacheValue = localStorage.getItem("performance_analysis_days")
        const value = cacheValue ? Number(cacheValue) : undefined
        if(value !== undefined){
            setPeriodFilter(value)
        }else{
            setPeriodFilter(7)
        }
    }

    const [periodFilter, setPeriodFilter] = useState<number>(
        localStorage.getItem("performance_analysis_days")
            ? Number(localStorage.getItem("performance_analysis_days"))
            : 7
    );
    const [fromDateTime, setFromDateTime] = useState<Date>();
    const [toDateTime, setToDateTime] = useState<Date>();
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isPeriodByDay, setIsPeriodByDay] = useState(false);
    const [isDateRangeValid, setIsDateRangeValid] = useState(true);

    const handlePeriodFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(event.target.value);

        if (value === null || value === undefined || value <= 0) {
            setPeriodFilter(1);
        } else {
            setPeriodFilter(value);
            localStorage.setItem("performance_analysis_days", value.toString());
        }
    }

    useEffect(() => {
        if((periodFilter != null || periodFilter !== undefined) && periodFilter <= DIFFERENCE_MAX_DAYS && validateNumber(periodFilter.toString())){
           console.log("Period filter changed to: ", periodFilter);

            const days = Number(periodFilter)
            const newStopDate = new Date();
            setToDateTime(newStopDate)

            const rawStartDate = subDays(newStopDate, days === 1 ? 0 : days);
            const newStartDate = startOfDay(rawStartDate);
            setFromDateTime(newStartDate)

            let periodWeek: Period = {groupBy: "week", fromDateTime: newStartDate, toDateTime: newStopDate};
            dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
            localStorage.setItem("performance_analysis_days", periodFilter.toString());
        }

    }, [periodFilter]);

    const statusColor = (status: MenuItemStatus | undefined) => {
        if (!status) {
            return <CircularProgress size={20} />
        }
        const color =  getIconColor(status);
        const sync = status.includes("Sincronizando");
        return <div style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            margin: "10px",
            float: "left",
            background: sync ? 'transparent' : color
        }} title={getStatusName(status)}>{sync ? <Sync style={{color: color}}  /> : ''}</div>
    }

    const verifyProgress = (value: number) => {
        if (value >= 70 && value <= 95) {
            return "#a4f657"
        }else if (value >= 60 || value > 95) {
            return "#FFD700";
        }else if (value < 60 ) {
            return "#FF4500"
        }
    };

    const handleNavigation = (event: MouseEvent<HTMLElement>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPowerStation(powerPlantId);
    }

    const BorderLinearProgress = styled(LinearProgress)(({ theme, value }) => ({        
        height: 20,
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey,
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: verifyProgress(value ?? 0),
        },
    }));

    const isLightMode = () => {
        return theme.palette.mode === 'light'
    }

    // @ts-ignore
    function LinearProgressWithLabel({value}) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                    <BorderLinearProgress variant="determinate" value={value}/>
                </Box>
                <Box sx={{ position: 'relative', minWidth: 35, right: '50%'}}>
                    <Typography variant="body2">{`${Math.round(value)}%`}</Typography>
                </Box>
            </Box>
        );
    }

    function irradianceSum (element: number) : number | string {
        return element ? parseFloat(element.toString()).toFixed(2) : " ";
    }
    function performanceRatio(element: number) : string{
        return element ? parseInt(element.toString()) + "%" : " ";
    }

    const GeneratedInjected = (generated:number, injected:number) => {
        return(
            <Box  >
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "#767676" : "#fff"}>Gerada: <label style={{color: isLightMode() ? "#000" : "#fff"}}>{generated ? `${generated.toFixed(2) + " MWh "}` : " "} </label></Typography>
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "#767676" : "#fff"}>Injetada: <label style={{color: isLightMode() ? "#000" : "#fff"}}>{injected ? `${injected.toFixed(2) + " MWh "}` : " "} </label></Typography>
            </Box>
        )
    }

    const structureRow = (entitiesValues: Array<Performance>) => {
        let entityAlarms: Array<any> = []
        entitiesValues.forEach( element => {
            const entity = entityMap.get(element.powerStationId);
            entityAlarms.push({
                status: statusColor(entity?.status),
                powerStationName: <Link href="#" onClick={(event) => handleNavigation(event, element.powerStationId)} underline="hover">{element.powerStationName}</Link>,
                powerCapacityNominal: element.powerCapacityNominal ? parseInt(element.powerCapacityNominal.toString()) : " ",
                instantPower: entity && entity.instantPowerList ? <div style={{width: '90%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <ActivePowerChart dataInstantPower={entity.instantPowerList} powerStation={element.powerStationName}/>
                    </Box>
                    <Box>{element.instantPower ? parseFloat(element.instantPower.toString()).toFixed(2) : " "}</Box>
                    
                </div>
                : <div style={{width: '90%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                    <Box>{element.instantPower ? parseFloat(element.instantPower.toString()).toFixed(2) : " "}</Box>
                </div>,
                irradianceSum: entity && entity.irradianceList ? <div style={{width: '80%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        { entity.irradianceList.length > 0 ?
                                            <IrradianceChart dataIrradiance={entity.irradianceList}/>
                                            : ""
                                        }
                                    </Box>
                                    <Box>{irradianceSum(element.irradianceSum)}</Box>
                                </div>
                                : <LinearProgress color="primary" />,

                generatedEnergySum: entity && entity.generatedEnergyList && entity.generatedEnergyList.length > 0 && entity.injectedEnergyList.length > 0 ?
                    <div style={{width: '80%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                        <Box>
                            <GeneratedInjectedChart dataGenerated={entity.generatedEnergyList} dataInjected={entity.injectedEnergyList}/>
                        </Box>
                        <Box>
                            {GeneratedInjected(
                                element.generatedEnergySum,
                                element.injectedEnergySum
                            )}
                        </Box>
                    </div> :
                    <Box>
                            {GeneratedInjected(
                                element.generatedEnergySum,
                                element.injectedEnergySum
                            )}
                    </Box>
,
                performanceRatio: <LinearProgressWithLabel value={element.performanceRatio || 0} /> ,
                availability: element.availability ? parseInt(element.availability.toString()) + " %" : " ",
                generatedEnergy: entity && entity.generatedEnergyList ? entity.generatedEnergyList : [],
            });
        });
        setRows(entityAlarms);
    }

    useEffect(() => {
        if(fromDateTime !== undefined && toDateTime !== undefined){
            let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
            dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
        }
    }, [pagedSearchParams])

    useInterval(() => {
        if(fromDateTime !== undefined && toDateTime !== undefined) {
            let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
            dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
        }
    }, userConfig.mainUpdateInterval) // 5 minutes

    useEffect(() => {
        setIsLoadingData(false);
        if(entities && entities.length > 0){
            entityMap.clear();
            // create a map grouping entities by powerStationId
            entities.forEach(entity => {
                entityMap.set(entity.powerStationId, entity);
            });
            if(powerStations && powerStations.length > 0){
                structureRow(powerStations);
            }
        }
    }, [entities])

    useEffect(() => {
        if(powerStations && powerStations.length > 0){
            structureRow(powerStations);
            if(fromDateTime !== undefined && toDateTime !== undefined) {
                let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
                setIsLoadingData(true);
                dispatch(searchAnalisysData({powerStationIds: powerStations.map(ps => ps.powerStationId), period: periodWeek, actualDay: period}))
            }
        }
        else{
            structureRow([]);
        }
        if (fromDateTime !== undefined && toDateTime !== undefined) {
            setIsFirstLoad(false);
        }
    }, [powerStations, theme])

    useEffect(() => {
        dispatch(clear());
        getPeriodFilter()
        setRows([])
    }, []);

    const onChangeHandleFromDateTimeChange = (newValue: Date | null | undefined) => {
        if (newValue && toDateTime) {
            const diffInDays = differenceInDays(toDateTime,newValue);
            if (diffInDays > DIFFERENCE_MAX_DAYS) {
                setIsDateRangeValid(false);
                setFromDateTime(newValue);
                return;
            }
            if (newValue) {
                setIsDateRangeValid(true);
                setFromDateTime(newValue);
            }
        }
    };

    const onChangeHandleToDateTimeChange = (newValue: Date | null | undefined) => {
        // verify if newValue is > DIFFERENCE_MAX_DAYS days from fromDateTime
        if (newValue && fromDateTime) {
            const diffInDays = differenceInDays(newValue, fromDateTime);
            if (diffInDays > DIFFERENCE_MAX_DAYS) {
                setIsDateRangeValid(false);
                setToDateTime(newValue);
                return;
            } else {
                setIsDateRangeValid(true);
                setToDateTime(newValue);
            }
        }
    };

    const handleDayButton = () => {
        setIsPeriodByDay(true);
        const now = new Date();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        setFromDateTime(startOfToday);
        setToDateTime(now);
        dispatch(searchPowerStations({
            period: { fromDateTime: startOfToday, toDateTime: now, groupBy: 'week' },
            actualDay: period,
            params: pagedSearchParams
        }));
    }

    const handleDatePickerChange = () => {
        setIsPeriodByDay(false);
        if(fromDateTime !== undefined && toDateTime !== undefined){
            let periodWeek: Period = {groupBy: "week", fromDateTime, toDateTime};
            dispatch(searchPowerStations({period: periodWeek, actualDay: period, params: pagedSearchParams}));
        }
    };
    const isMediumScreen = window.innerWidth <= 1340;
    return (
        <Grid container>
            <Grid item xs={12}>
                <ASectionTitle styleContainer={{flexDirection:isMediumScreen ? 'column' : 'row', padding: '0 0 32px 0',justifyContent: 'flex-start', }}
                    titleLabel="Análise de Desempenho"
                    subTitleLabel={isMediumScreen ? " " : ""}
                    contentChildrenStyle={{justifyContent: isMediumScreen ? 'flex-start' : 'flex-end'}}
                    styleContainerTitle={{ display: 'flex',
                         justifyContent: 'space-between',
                           alignItems: 'flex-end', padding: '0px', marginBottom: !isDateRangeValid || periodFilter > DIFFERENCE_MAX_DAYS ? '-8px' : '0px'
                    }}

                    contentChildren={
                        <LocalizationProvider dateAdapter={AdapterDateFns} sx={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-around',
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: isMediumScreen ? 'column' : 'row',
                                justifyContent: 'space-around'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'space-around',}}>

                                <Box sx={{ width: extraSmallScreen ? 170 : 230, paddingRight: '16px', borderRadius: '14px' }}>
                                    <DateTimePicker
                                        label="De:"
                                        renderInput={(params) => <TextField sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '40px' } }} {...params} />}
                                        value={fromDateTime}
                                        ampm={false}
                                        views={['day']}
                                        inputFormat='dd/MM/yyyy'
                                        onChange={(newValue) => onChangeHandleFromDateTimeChange(newValue)}
                                    />
                                </Box>
                                <Box sx={{ width: extraSmallScreen ? 170 : 230, paddingRight: '16px',  }}>
                                    <DateTimePicker
                                        label="Até:"
                                        renderInput={(params: any) =>
                                            <TextField
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '40px', } }}
                                                {...params}
                                            />
                                        }
                                        value={toDateTime}
                                        ampm={false}
                                        views={['day']}
                                        inputFormat='dd/MM/yyyy'
                                        onChange={(newValue) => onChangeHandleToDateTimeChange(newValue)}
                                    />{(!isDateRangeValid) && (
                                        <FormHelperText error={!isDateRangeValid}>Limite máximo permitido de {DIFFERENCE_MAX_DAYS} dias.</FormHelperText>
                                    )}
                                </Box>
                                <Box style={{ width: isMediumScreen ? 75 : 100, marginRight: '16px' }}>
                                    <FormControl error={periodFilter == null}>
                                        <TextField
                                        size="small"
                                            sx={{ width: isMediumScreen ? 75 : 100, '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '40px' } }}
                                            type="number"
                                            label={isMediumScreen ? "Últ. dias" : "Últimos dias"}
                                            inputProps={{ min: 1, max: DIFFERENCE_MAX_DAYS }}
                                            value={periodFilter}
                                            placeholder=""
                                            title={"Informe um número de 1 a " + DIFFERENCE_MAX_DAYS}
                                            onChange={handlePeriodFilter}
                                            error={periodFilter == null || periodFilter > DIFFERENCE_MAX_DAYS}
                                        />
                                        {(periodFilter == null || periodFilter > DIFFERENCE_MAX_DAYS) && (
                                            <FormHelperText error id="period-helper-text">Limite máximo permitido de {DIFFERENCE_MAX_DAYS} dias.</FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>

                                {<ButtonGroup aria-label="Basic button group" sx={{
                                     height: '40px', width: window.innerWidth <= 650 ? '100%' : (window.innerWidth > 651 && window.innerWidth < 900) ? '30%' : undefined }}>

                                    <LoadingButton
                                        size="small"
                                        sx={{ minWidth: '40px', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}
                                        variant={isPeriodByDay ? "contained" : "outlined"}
                                        onClick={handleDayButton}
                                        loading={loading && !isLoadingData}
                                        loadingPosition="end"
                                        disabled={!isDateRangeValid || (periodFilter != null && periodFilter > DIFFERENCE_MAX_DAYS)}
                                    >
                                        Dia
                                    </LoadingButton>
''
                                    <LoadingButton
                                        onClick={handleDatePickerChange}
                                        size="small"
                                        variant={!isPeriodByDay ? "contained" : "outlined"}
                                        sx={{ minWidth: '20px', borderTopRightRadius: '8px', borderBottomRightRadius: '8px' }}
                                        loading={loading && !isLoadingData}
                                        loadingPosition="end"
                                        disabled={!isDateRangeValid || (periodFilter != null && periodFilter > DIFFERENCE_MAX_DAYS)}
                                    >
                                        Período
                                    </LoadingButton>
                                </ButtonGroup>
                                }
                                </Box>
                            </Box>

                        </LocalizationProvider>
                    }
                />

            </Grid>
            <Grid item xs={12}>
                <ACard
                    key={`performance-analysis-${openSubMenu}`}
                    // title="Análise de Desempenho"
                    styleProps={{
                        headerStyle: {
                            display: 'none',
                            backgroundColor: 'rgb(247, 247, 247)'
                        },
                        footerStyle: { padding: 18, paddingTop: 0 },
                        contentStyle: { padding: '0 16px 10px 0' },
                    }}
                >
                    <ADataGrid
                        hideSelection={true}
                        ariaLabel={'overview-performance-analysis'}
                        hideFilters={true}
                        size={"small"}
                        columns={performanceAnalysisColumns}
                        rows={rows}
                        headerStyle={{ color: '#fff'}}
                        page={pagedSearchParams.page}
                        loading={loading && !isLoadingData && isFirstLoad}
                        totalOfRecords={totalOfRecords || 0}
                        rowsPerPage={pagedSearchParams.size}
                        onFiltersChange={handleOnFiltersChange}
                        showSortOptions={true}
                    />
                </ACard>
            </Grid>

        </Grid>

    )

}

export default PerformanceAnalysisPage;